/* @flow */

import * as React from 'react';

import { connect } from 'react-redux';

import {
  addProduct,
  addDiscount,
  getCart,
  selectOneProduct,
  selectCartUUID as selectCartId,
} from '@gandi/cart';
import { useTranslator, type TranslatorProps } from '@gandi/translate';

import { Base, Cell, Text } from '@gandi/ui';
import { ListRow, ListItem, Action } from '@gandi/ui/deprecated';

import {
  getBaseContact,
  setPatchContactsFlag,
} from 'src/Cart/DomainContacts/Wizard/modules/actions';

import type { Dispatch, FullState, GetState } from '../../../types';
import {
  fetchDomainCreateProductsCatalog,
  fetchClaims,
} from '../../modules/actions';

import { REFER_A_FRIEND_REASON, stringify } from './helpers';

import type { CartProductSuggestion } from './types';

import styles from './CartSuggest.scss';

export type OwnProps = {
  suggestion: CartProductSuggestion,
};

export type StateProps = {
  cartId: string,
};

export type InjectedProps = {
  dispatch: Dispatch,
};

export type Props = {
  ...OwnProps,
  ...StateProps,
  ...InjectedProps,
};

const addCartSuggestProduct =
  (suggestion: CartProductSuggestion, translator: TranslatorProps) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const { product_type, process, payload, price, name } = suggestion;

    const domainCreate = product_type === 'domain' && process === 'create';

    const productData = {
      product_type,
      process,
      payload: { ...payload },
    };

    const extra = { label: name };

    if (!('duration' in productData.payload) && price.min_duration) {
      productData.payload.duration = price.min_duration;
    }

    if (domainCreate) {
      productData.payload.owner = await dispatch(getBaseContact(translator));
    }

    const code = suggestion.discount?.code;
    const relatedProduct = suggestion.discount?.related_product;
    if (code && typeof relatedProduct === 'number') {
      await dispatch(addDiscount(code, relatedProduct)).catch(() => {});
    } else if (code && productData.product_type === 'domain') {
      await dispatch(addDiscount(code)).catch(() => {});
    }

    await dispatch(
      addProduct({
        productData,
        extra,
        options: {
          headers: { 'X-Orga-Reset': 0 },
        },
      }),
    );

    if (domainCreate) {
      // $FlowFixMe[not-an-array]
      dispatch(setPatchContactsFlag(false));
      const addedProduct = selectOneProduct(getState(), { fqdn: payload.fqdn });

      if (addedProduct) {
        const canHaveClaims = addedProduct.payload.tld_phase !== 'sunrise';
        const params = [addedProduct];

        // $FlowFixMe improve @gandi/cart Product
        dispatch(fetchDomainCreateProductsCatalog(params));
        if (canHaveClaims) {
          // $FlowFixMe improve @gandi/cart Product
          dispatch(fetchClaims(params));
        }
      }
    }
  };

function getTranslations(
  suggestion: CartProductSuggestion,
  __: TranslatorProps['__'],
) {
  let label;
  let description;

  // eslint-disable-next-line default-case
  switch (suggestion.reason) {
    case 'domain_available':
      label = (suggestion.payload.fqdn: ?string);
      break;
    case 'refer_a_friend_shs_offer':
      label = __('Get 6 months of free hosting without engagement!');
      description = __('Try Simple Hosting');
      break;
  }

  return label ? { label, description } : undefined;
}

function renderPrice(
  { price }: CartProductSuggestion,
  translator: TranslatorProps,
) {
  const amount = price.price_after_taxes;
  const currency = price.currency;
  const minDuration = price.min_duration;
  const discount = price.discount;
  if (Number.isNaN(amount)) {
    return null;
  }

  const { __, formatPrice } = translator;

  const durationText = discount
    ? __('%(count)s years', { count: minDuration })
    : minDuration > 1
      ? __('%(count)s years', { count: minDuration })
      : __('/year');

  return (
    <span>
      <span>{formatPrice(amount, currency)}</span>{' '}
      <Text color="light">{durationText}</Text>
    </span>
  );
}

function renderNormalPrice(
  { price }: CartProductSuggestion,
  translator: TranslatorProps,
) {
  const amount = price.price_after_taxes;
  const normalAmount = price.normal_price_after_taxes;
  const currency = price.currency;

  if (Number.isNaN(amount) || !normalAmount || !(amount < normalAmount)) {
    return null;
  }

  const { formatPrice } = translator;
  return (
    <Text size="small" color="light" decoration="line-through">
      {formatPrice(normalAmount, currency)}
    </Text>
  );
}

function renderRebate({ price }: CartProductSuggestion) {
  const amount = price.price_after_taxes;
  const normalAmount = price.normal_price_after_taxes;

  if (!amount || !normalAmount || !(amount < normalAmount)) {
    return null;
  }

  const rebate = Math.round(((normalAmount - amount) * 100) / normalAmount);

  return <span className={styles.rebate}>{`-${rebate}%`}</span>;
}

function Product(props: Props): null | React.Node {
  const { dispatch, cartId, suggestion } = props;

  const translator = useTranslator();
  const { __ } = translator;

  const [loading, setLoading] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);

  const translations = getTranslations(suggestion, __);
  if (!translations || dismissed) {
    return null;
  }

  const handleClick = async (
    evt: SyntheticMouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    const noop = evt.currentTarget.tagName === 'a';

    if (noop) {
      return;
    }

    evt.preventDefault();
    setLoading(true);

    try {
      await dispatch(addCartSuggestProduct(suggestion, translator));
      await dispatch(getCart());
    } finally {
      setLoading(false);
      setDismissed(true);
    }
  };

  const params = {
    cross_sell: 1,
    discountCode: suggestion.discount?.code,
    reason: suggestion.reason,
    ...suggestion.payload,
  };
  const queryString = stringify(params);

  const to =
    suggestion.reason !== REFER_A_FRIEND_REASON
      ? `/simplehosting/create/${String(cartId)}${queryString}`
      : undefined;

  return (
    <ListRow separatorBottom>
      <ListItem>
        <Cell flexGrow1>{translations.label}</Cell>
        <Cell>
          {translations.description || ''}
          {suggestion.reason !== REFER_A_FRIEND_REASON && (
            <Base flex tagName="span">
              {renderRebate(suggestion)}
              <Base flex flexCol tagName="span">
                {renderNormalPrice(suggestion, translator)}
                {renderPrice(suggestion, translator)}
              </Base>
            </Base>
          )}
        </Cell>
      </ListItem>
      <Action onClick={handleClick} to={to} loading={loading} icon="plus">
        {__('Add to cart')}
      </Action>
    </ListRow>
  );
}

const mapStateToProps = (state: FullState): StateProps => ({
  cartId: selectCartId(state),
});

// $FlowFixMe[signature-verification-failure]
// $FlowFixMe[underconstrained-implicit-instantiation]
export default connect(mapStateToProps)(Product);
